// ─── DeskeyVideoStudio ──────────────────────────────────────────────────────
// ONE foundational entry point that REPLACES Deskeynomics + Visual Explainer.
//
// Flow:
//   ① Setup   — mode toggle (Explain / Story) + language picker → Cook
//   ② Cooking — full-bleed CinematicStove animation. Pipeline internals are
//               HIDDEN; the only visible state is "still cooking".
//   ③ Ready   — embedded Prime-style <video> player with chapter rail, speed
//               control, and the lesson logline as a cinematic intro.
//
// The whole component is self-contained — no shared state with Deskeynomics
// (those routes can be quietly retired once this is rolled out).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { featureInk } from './featureColors';
import {
  Sparkles, Languages, Play, Pause, Volume2, VolumeX, Film, X,
  ChevronLeft, ChevronRight, FileText, History, Trash2, BookOpen, Wand2,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { notifyError } from '../../../../lib/errors';
import api from '../../studentApi';
import CinematicProduction from '../components/CinematicProduction';
import GenrePickerModal from '../components/GenrePickerModal';
import LightsToggle from '../components/LightsToggle';
import { confirmDialog } from '../../../../components/ConfirmDialog';
import Loader from '../../../../components/Loader';

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOK = () => localStorage.getItem('desky_token') || '';

// Persisted (survives a full reload / browser reopen, unlike component
// state) — which gen_ids the student has already explicitly closed out of
// for this lesson. Without this, a video finished days ago would keep
// popping back up as if it were a fresh result every time the student
// reopens this lesson's Video Studio tab.
const _dismissedKey = (lessonId) => `desky_video_dismissed_${lessonId}`;
const _loadDismissed = (lessonId) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(_dismissedKey(lessonId)) || '[]'));
  } catch { return new Set(); }
};
const _saveDismissed = (lessonId, ids) => {
  try { localStorage.setItem(_dismissedKey(lessonId), JSON.stringify([...ids])); } catch { /* ignore storage errors */ }
};

const TINT = '#F08A24';
const PRIME_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const SKIP_SECONDS = 10;

// Single cohesive theme — both modes share the studio's amber accent so the
// surface reads like one cinematic family, not a rainbow. Variation comes
// from typography + iconography, not from chromatic shock.
const STUDIO_ACCENT = '#F08A24';   // Apple amber — the only accent on every surface
const MODES = [
  {
    id: 'explain',
    label: 'Explain Mode',
    icon: Wand2,
    tagline: 'Crystal-clear breakdown. Hook → concept → mechanism → fact → takeaway.',
    good: ['STEM concepts', 'How-it-works', 'Fast review'],
    accent: STUDIO_ACCENT,
  },
  {
    id: 'story',
    label: 'Story Mode',
    icon: BookOpen,
    tagline: 'A short story where the lesson IS the plot — learn by following it.',
    good: ['Humanities', 'Concept-in-context', 'Memorable'],
    accent: STUDIO_ACCENT,
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// Root view — orchestrates the 3-step flow
// ═══════════════════════════════════════════════════════════════════════════
export default function DeskeyVideoStudio({ lessonId, lessonTitle }) {
  // Light by default. Cinema mode is an opt-in immersive toggle, not a
  // default surface — the studio should read as a clean, human-designed
  // Deskey workspace from the moment it opens.
  const [cinemaOn, setCinemaOn] = useState(false);

  // Broadcast cinema mode to <html> so the global App.css `html.cinema-mode`
  // CSS-variable cascade kicks in (top of the document tree).
  // CRITICAL: the StudentApp shell sets `--st-bg`, `--st-card`, etc. as
  // INLINE styles (themeToCssVars). Inline styles beat stylesheet rules on
  // the same element, which means the html.cinema-mode CSS override loses
  // there — the surrounding chrome (top bar, side rails, lesson page)
  // stays white. We fix that here by mutating the inline tokens directly:
  // when cinema flips on we stash the originals and write the dark palette
  // onto the shell; when it flips off we restore the originals.
  useEffect(() => {
    const html  = document.documentElement;
    const shell = document.querySelector('[data-testid="student-shell"]');
    if (cinemaOn) {
      html.classList.add('cinema-mode');
      if (shell && !shell.dataset.cinemaStash) {
        const tokens = ['--st-bg', '--st-card', '--st-card-2',
                        '--st-border', '--st-ink', '--st-mute'];
        const stash = {};
        tokens.forEach(t => { stash[t] = shell.style.getPropertyValue(t); });
        shell.dataset.cinemaStash = JSON.stringify(stash);
        shell.style.setProperty('--st-bg',     '#000000');
        shell.style.setProperty('--st-card',   '#0A0A0C');
        shell.style.setProperty('--st-card-2', '#121216');
        shell.style.setProperty('--st-border', '#1F1F24');
        shell.style.setProperty('--st-ink',    '#F5F5F7');
        shell.style.setProperty('--st-mute',   '#8E8E93');
      }
    } else {
      html.classList.remove('cinema-mode');
      if (shell?.dataset.cinemaStash) {
        try {
          const stash = JSON.parse(shell.dataset.cinemaStash);
          Object.entries(stash).forEach(([k, v]) => {
            if (v) shell.style.setProperty(k, v);
            else   shell.style.removeProperty(k);
          });
        } catch { /* ignore */ }
        delete shell.dataset.cinemaStash;
      }
    }
    return () => {
      // On unmount — always restore (we never want to leave the page locked dark).
      html.classList.remove('cinema-mode');
      if (shell?.dataset.cinemaStash) {
        try {
          const stash = JSON.parse(shell.dataset.cinemaStash);
          Object.entries(stash).forEach(([k, v]) => {
            if (v) shell.style.setProperty(k, v);
            else   shell.style.removeProperty(k);
          });
        } catch { /* ignore */ }
        delete shell.dataset.cinemaStash;
      }
    };
  }, [cinemaOn]);

  const [active, setActive] = useState(null);   // currently playing/in-production video gen_id
  const [tab, setTab] = useState('new');        // "new" | "library"
  // gen_ids the student explicitly dismissed for THIS lesson — persisted to
  // localStorage (not just component state) so a manually-closed result
  // doesn't keep popping back up after a full page reload/browser reopen,
  // while a still-cooking or not-yet-seen finished video still does.
  const [dismissed, setDismissed] = useState(() => _loadDismissed(lessonId));
  const dismiss = useCallback((id) => {
    setDismissed(prev => {
      const next = new Set(prev);
      next.add(id);
      _saveDismissed(lessonId, next);
      return next;
    });
  }, [lessonId]);

  // Re-resolve active video doc whenever active id changes (or page mounts)
  const [video, setVideo] = useState(null);

  const fetchVideo = useCallback(async (id) => {
    if (!id) return null;
    try {
      const v = await api.get(`/student/video/${id}`);
      setVideo(v);
      return v;
    } catch {
      // CRITICAL: do NOT clear active/video here. If the backend hasn't
      // committed the insert yet (sub-50ms race after onCooked), this
      // call 404s — clearing state would yank the production view away
      // mid-render and the student would see the setup screen flash
      // back. Polling will refresh the doc once it's live.
      return null;
    }
  }, []);

  // We only need to fetch the FULL doc when we've been handed an `active`
  // id without a video doc (e.g. user clicked a library item with just an
  // id). After `onCooked` or `auto-attach`, `video` is already populated,
  // so we skip the round-trip and rely on the polling loop for refreshes.
  useEffect(() => {
    if (active && !video) fetchVideo(active);
  }, [active, video, fetchVideo]);

  // ── Auto-attach to the most recent video for this lesson on mount,
  // whatever state it's in. The backend keeps producing even when this
  // component (or the whole browser) is closed — so when the student
  // comes back we silently re-attach to it: still cooking → Production
  // screen; finished (ready/failed) while they were away and not yet
  // seen → Ready/Failed screen. Either way they see it without having to
  // dig into Library. Only a video the student already explicitly closed
  // (persisted `dismissed`, see above) is skipped.
  //
  // CRITICAL: this is a ONE-SHOT mount effect. Earlier it ran with
  // `[lessonId, active, dismissed]` as deps and could re-fire while the
  // user was producing — racing with `onCooked` and momentarily yanking
  // the production view back to the Setup screen. By keying off only
  // `lessonId` (which is stable per route) and short-circuiting if we
  // already have an `active` id, we guarantee a single attach per visit
  // and prevent any flicker between Setup → Production → Setup.
  const attachedRef = useRef(false);
  useEffect(() => {
    if (!lessonId) return;
    if (attachedRef.current) return;
    attachedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/student/lesson/${lessonId}/videos`);
        if (cancelled) return;
        // If the user already clicked Produce while we were fetching, do
        // NOT override the active gen_id with whatever the catalog says.
        if (active) return;
        // Items come back sorted most-recent-first — the first undismissed
        // one (cooking, ready, or failed) is what the student left behind.
        const toResume = (r?.items || []).find(it => !dismissed.has(it.id));
        if (toResume) {
          setActive(toResume.id);
          setVideo(toResume);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  // Status poll while in production
  useEffect(() => {
    if (!active || !video) return;
    if (video.status !== 'cooking') { return; }
    let cancelled = false;
    const loop = async () => {
      while (!cancelled) {
        await new Promise(r => setTimeout(r, 2000));
        if (cancelled) return;
        try {
          const s = await api.get(`/student/video/${active}/status`);
          if (s.status === 'ready') {
            const full = await fetchVideo(active);
            if (full?.status === 'ready') return;
          } else if (s.status === 'failed') {
            await fetchVideo(active);
            return;
          }
        } catch { /* ignore transient errors */ }
      }
    };
    loop();
    return () => { cancelled = true; };
  }, [active, video, fetchVideo]);

  // Optimistic kick-off — render the production stage IMMEDIATELY when
  // the student presses the button, instead of leaving a blank gap until
  // the first fetchVideo round-trip resolves.
  const onCooked = useCallback((id, modeHint = 'explain', genreId = null) => {
    setActive(id);
    setVideo({
      id,
      status:        'cooking',
      mode:          modeHint,
      genre:         genreId,
      lesson_id:     lessonId,
      lesson_title:  lessonTitle,
      title:         '',
      logline:       '',
      panel_count:   0,
      started_at:    new Date().toISOString(),
    });
  }, [lessonId, lessonTitle]);

  // "View Library" while production continues in the background. We DO add
  // the gen_id to `dismissed` so auto-attach in THIS mount doesn't bounce
  // us straight back to the loader — but `dismissed` is component-local, so
  // a true tab remount (LearnNowPage swap) resets it and we re-attach
  // correctly on the next mount.
  const onViewLibrary = useCallback(() => {
    if (active) {
      setDismissed(prev => {
        const next = new Set(prev);
        next.add(active);
        return next;
      });
    }
    setActive(null);
    setVideo(null);
    setTab('library');
  }, [active]);

  // Only reachable from ReadyScreen/FailedCard (a TERMINAL state) — the
  // student has actually seen the result, so this dismissal is persisted
  // (see `dismiss` above): it must survive a reload, unlike
  // onViewLibrary's session-only one below (that fires while still
  // cooking, and the student expects to come back to it).
  const onReset = useCallback(() => {
    if (active) dismiss(active);
    setActive(null);
    setVideo(null);
  }, [active, dismiss]);

  return (
    <div
      data-testid="deskey-video-studio"
      data-cinema={cinemaOn ? 'on' : 'off'}
      style={{
        background: 'var(--st-bg)',
        color:      'var(--st-ink)',
        minHeight:  '100%',
      }}>

      <div className="px-6 sm:px-10 pt-6 pb-8 space-y-6">
        {/* Editorial tab bar */}
        {!active && (
          <div className="flex items-center gap-6 border-b"
            style={{ borderColor: 'var(--st-border)' }}
            data-testid="video-studio-tabs">
            <EditorialTab id="new"     active={tab} onClick={() => setTab('new')}     label="Commission" />
            <EditorialTab id="library" active={tab} onClick={() => setTab('library')} label="Library" />
          </div>
        )}

        {/* Body */}
        {active && video?.status === 'cooking' && (
          <ProductionScreen video={video} lessonTitle={lessonTitle}
            onViewLibrary={onViewLibrary} />
        )}
        {active && video?.status === 'ready' && (
          <ReadyScreen video={video} onClose={onReset} />
        )}
        {active && video?.status === 'failed' && (
          <FailedCard video={video} onReset={onReset} />
        )}

        {!active && tab === 'new' && (
          <SetupScreen lessonId={lessonId} lessonTitle={lessonTitle}
            cinema={cinemaOn} onCooked={onCooked} />
        )}
        {!active && tab === 'library' && (
          <LibraryScreen lessonId={lessonId} cinema={cinemaOn} onOpen={(item) => {
            setVideo(item);
            setActive(item.id);
          }} />
        )}
      </div>

      {cinemaOn && <LightsToggle onExit={() => setCinemaOn(false)} />}
    </div>
  );
}

// Editorial tab — sharp underline below the active label, no pills, no
// fill colours. Reads like a printed-magazine section header.
function EditorialTab({ id, active, onClick, label }) {
  const isActive = active === id;
  return (
    <button onClick={onClick}
      data-testid={`video-studio-tab-${id}`}
      className="relative py-3 text-xs font-semibold transition-colors"
      style={{ color: isActive ? 'var(--st-ink)' : 'var(--st-mute)' }}>
      {label}
      <span aria-hidden="true"
        className="absolute left-0 right-0 -bottom-px h-[2px] transition-opacity"
        style={{ background: 'var(--st-ink)', opacity: isActive ? 1 : 0 }} />
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Setup screen — mode toggle + language → Produce (Story mode pops a
// genre picker first so the entire film commits to one visual language).
// ═══════════════════════════════════════════════════════════════════════════
function SetupScreen({ lessonId, lessonTitle, onCooked }) {
  const [mode, setMode] = useState('explain');
  const [language, setLanguage] = useState('en');
  const [languages, setLanguages] = useState([{ id: 'en', label: 'English' }]);
  const [genres, setGenres] = useState([]);
  const [busy, setBusy] = useState(false);
  const [genreModalOpen, setGenreModalOpen] = useState(false);
  // Story-mode topic brief — after the student picks a genre we DON'T cook
  // immediately. We surface a Netflix-style "About this title" hero with
  // the lesson + genre + ambient artwork so they can confirm with a Play
  // press. This is the "topic before" the user asked for.
  const [previewGenre, setPreviewGenre] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const meta = await api.get('/student/video/meta/modes');
        if (meta?.languages?.length) setLanguages(meta.languages);
        if (meta?.genres?.length)    setGenres(meta.genres);
      } catch { /* silent */ }
    })();
  }, []);

  // Actually fire the cook. Called either directly (Explain mode) or after
  // the student confirms a genre via the topic brief hero (Story mode).
  const fireCook = async (genreId = null) => {
    if (busy) return;
    setBusy(true);
    try {
      const body = { mode, language };
      if (genreId) body.genre = genreId;
      const r = await api.post(`/student/lesson/${lessonId}/video/cook`, body);
      if (r?.gen_id) {
        toast.success('Production has commenced. Your film will be delivered to your Library when ready.');
        onCooked(r.gen_id, mode, r.genre || genreId || null);
      } else {
        toast.error("Couldn't start making your video. Please try again.");
      }
    } catch (e) {
      notifyError(e, "Couldn't start making your video. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Click handler on the Produce button. Story mode → open genre picker;
  // Explain mode → cook immediately (genre is irrelevant for explainers).
  const onProduce = () => {
    if (busy) return;
    if (mode === 'story') {
      setGenreModalOpen(true);
    } else {
      fireCook(null);
    }
  };

  const activeMode = MODES.find(m => m.id === mode);

  // ── Story-mode topic brief — Netflix "About this title" hero ─────────────
  if (previewGenre) {
    return (
      <StoryTopicBrief
        lessonTitle={lessonTitle}
        genre={previewGenre}
        languageLabel={(languages.find(l => l.id === language) || {}).label}
        busy={busy}
        onBack={() => setPreviewGenre(null)}
        onConfirm={() => fireCook(previewGenre.id)}
      />
    );
  }

  return (
    <div data-testid="video-studio-setup" className="space-y-6">
      {/* ── Editorial hero — restrained, designed-feel, NOT a gradient slab.
          A small accent rule + thoughtful typography reads like a printed
          brief from the studio rather than an AI welcome card. ──────────── */}
      <header className="relative rounded-2xl border p-6 sm:p-7"
        style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)' }}>
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-12 h-12 rounded-xl items-center justify-center shrink-0"
            style={{
              background: `color-mix(in srgb, ${activeMode.accent} 14%, transparent)`,
              color:      activeMode.accent,
            }}>
            <Film className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-block w-6 h-[2px] rounded-full"
                style={{ background: activeMode.accent }} />
              <span className="text-xs font-semibold "
                style={{ color: featureInk(activeMode.accent) }}>
                Deskey Video Studio
              </span>
            </div>
            <h1 className="text-[22px] sm:text-[26px] font-semibold leading-[1.15] "
              style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
              Commission a short film of <span style={{ color: featureInk(activeMode.accent) }}>“{(lessonTitle || 'this lesson').slice(0, 56)}”</span>
            </h1>
            <p className="text-sm mt-2 max-w-2xl leading-relaxed"
              style={{ color: 'var(--st-mute)' }}>
              Choose a format. The writers' room drafts the script, the
              cinematography team renders each shot, the score is composed,
              and the master is delivered to your Library — entirely
              hands-off, 2 to 5 minutes long.
            </p>
          </div>
        </div>
      </header>

      {/* Mode toggle — two large cards */}
      <SectionHeader title="Choose the mode"
        subtitle="The framework adapts itself to the lesson's complexity (2-5 min)." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="video-studio-mode-grid">
        {MODES.map(m => {
          const isSel = mode === m.id;
          const Icon = m.icon;
          return (
            <button key={m.id} onClick={() => setMode(m.id)}
              data-testid={`video-studio-mode-${m.id}`}
              aria-pressed={isSel}
              className="group relative overflow-hidden rounded-2xl text-left p-4 sm:p-5 border-2 transition isolate"
              style={{
                minHeight: 168,
                borderColor: isSel ? m.accent : 'var(--st-border)',
                background:  isSel
                  ? `color-mix(in srgb, ${m.accent} 10%, var(--st-card))`
                  : 'var(--st-card)',
                color:       'var(--st-ink)',
                boxShadow:   isSel ? `0 16px 32px -18px ${m.accent}` : 'none',
              }}>
              {/* Subtle hand-drawn-style accent corner — softens the AI feel */}
              {isSel && (
                <span aria-hidden="true"
                  className="absolute top-3 right-3 inline-block w-2 h-2 rounded-full"
                  style={{ background: m.accent }} />
              )}
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
                  style={{
                    background: `color-mix(in srgb, ${m.accent} ${isSel ? 22 : 14}%, transparent)`,
                    color:      m.accent,
                  }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold "
                    style={{ color: featureInk(m.accent) }}>
                    {isSel ? 'Selected' : 'Mode'}
                  </div>
                  <div className="text-[17px] font-semibold mt-0.5"
                    style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
                    {m.label}
                  </div>
                  <div className="text-xs mt-1 leading-snug"
                    style={{ color: 'var(--st-mute)' }}>{m.tagline}</div>
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {m.good.map((g, i) => (
                      <span key={i} className="text-xs font-semibold px-2 py-0.5 rounded-full"
                        style={{
                          background: 'var(--st-bg)',
                          color:      'var(--st-mute)',
                          border:     '1px solid var(--st-border)',
                        }}>
                        {g}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Language */}
      <SectionHeader title="Narration language"
        subtitle="The script translates, but visuals stay culturally neutral." />
      <div className="flex flex-wrap gap-1.5" data-testid="video-studio-lang-row">
        {languages.slice(0, 16).map(l => (
          <button key={l.id} onClick={() => setLanguage(l.id)}
            data-testid={`video-studio-lang-${l.id}`}
            aria-pressed={language === l.id}
            className="px-3 py-1 rounded-full text-xs font-semibold border transition"
            style={{
              borderColor: language === l.id ? activeMode.accent : 'var(--st-border)',
              background: language === l.id ? activeMode.accent : 'transparent',
              color: language === l.id ? '#fff' : 'var(--st-ink)',
            }}>
            {l.label}
          </button>
        ))}
      </div>

      {/* CTA */}
      <div className="pt-4 mt-1 border-t flex items-center justify-between gap-3 flex-wrap"
        style={{ borderColor: 'var(--st-border)' }}>
        <div className="text-xs" style={{ color: 'var(--st-mute)' }}>
          The script supervisor determines the runtime — typically two to five
          minutes — to cover the lesson without padding or truncation.
        </div>
        <button onClick={onProduce} disabled={busy}
          data-testid="video-studio-cook"
          className="rounded-full px-5 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-50 transition-transform "
          style={{ background: activeMode.accent, boxShadow: `0 12px 28px -12px ${activeMode.accent}` }}>
          <Sparkles className="w-3.5 h-3.5" />
          {busy ? 'Initialising production…' : `Produce ${mode === 'story' ? 'Story film' : 'Explainer'}`}
        </button>
      </div>

      {/* Story-mode genre picker — opens only when mode === 'story'. Picking
          a genre now stages a topic brief preview (Netflix-style) instead
          of firing the cook immediately. */}
      <GenrePickerModal
        open={genreModalOpen}
        genres={genres}
        lessonId={lessonId}
        onClose={() => setGenreModalOpen(false)}
        onConfirm={(genreId) => {
          setGenreModalOpen(false);
          const g = genres.find(x => x.id === genreId);
          if (g) setPreviewGenre(g);
        }}
      />
    </div>
  );
}

// ── Story Topic Brief — clean designed brief, light surface ────────────────
// After the student picks a genre, we surface a clean brief card showing
// the lesson title, the chosen genre, and a single Play CTA. Confirms the
// commission before the camera rolls. Cohesive amber accent on light.
function StoryTopicBrief({ lessonTitle, genre, languageLabel, busy, onBack, onConfirm }) {
  const accent = genre.accent || genre.gradient?.[0] || STUDIO_ACCENT;
  return (
    <div data-testid="video-studio-story-brief" className="space-y-5">
      <div className="relative overflow-hidden"
        style={{
          borderRadius: 16,
          background:   'var(--st-card)',
          border:      `1.5px solid var(--st-border)`,
          boxShadow:    '0 8px 24px -16px rgba(15,23,42,0.18)',
        }}>
        <span aria-hidden="true" className="absolute top-0 left-0 right-0 h-1"
          style={{ background: accent }} />
        <div className="p-7 sm:p-9">
          <div className="inline-flex items-center gap-2 text-xs font-semibold "
            style={{ color: featureInk(accent) }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
            Now Producing · {genre.label}
          </div>
          <h2 className="text-[26px] sm:text-[34px] lg:text-[40px] font-semibold leading-[1.05] mt-2 max-w-3xl"
            style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
            &ldquo;{lessonTitle}&rdquo;
          </h2>
          <p className="text-xs sm:text-[13.5px] mt-3 max-w-2xl leading-relaxed"
            style={{ color: 'var(--st-mute)' }}>
            {genre.tagline || genre.blurb || 'A short film where the lesson IS the plot — learn by following it.'}
          </p>

          <div className="flex flex-wrap items-center gap-3 mt-5">
            <button onClick={onConfirm} disabled={busy}
              data-testid="video-studio-story-brief-play"
              className="inline-flex items-center gap-2 px-6 py-3 text-xs font-semibold transition-transform  disabled:opacity-50"
              style={{
                background:   STUDIO_ACCENT,
                color:        '#fff',
                borderRadius: 999,
                boxShadow:    `0 12px 28px -10px ${STUDIO_ACCENT}88`,
              }}>
              <Play className="w-3.5 h-3.5" />
              {busy ? 'Rolling camera' : 'Begin Production'}
            </button>
            <button onClick={onBack} data-testid="video-studio-story-brief-back"
              className="inline-flex items-center gap-2 px-5 py-3 text-xs font-semibold transition-colors"
              style={{
                background: 'var(--st-bg)',
                color:      'var(--st-ink)',
                borderRadius: 999,
                border:     '1px solid var(--st-border)',
              }}>
              Change genre
            </button>
            <span className="text-xs font-semibold ml-1"
              style={{ color: 'var(--st-mute)' }}>
              {languageLabel || 'English'} · 2-5 min runtime
            </span>
          </div>
        </div>
      </div>

      {/* Production notes — call-sheet trio. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="video-studio-story-brief-notes">
        <BriefField label="Format"   value={`Story · ${genre.label}`} />
        <BriefField label="Language" value={languageLabel || 'English'} />
        <BriefField label="Runtime"  value="Auto-determined by AI" />
      </div>
    </div>
  );
}

function BriefField({ label, value }) {
  return (
    <div className="p-3.5"
      style={{
        background: 'var(--st-card)',
        border:     '1px solid var(--st-border)',
        borderRadius: 12,
      }}>
      <div className="text-xs font-semibold "
        style={{ color: 'var(--st-mute)' }}>
        {label}
      </div>
      <div className="text-xs font-semibold mt-1 truncate"
        style={{ color: 'var(--st-ink)', fontFamily: 'var(--st-font-heading)' }}>
        {value}
      </div>
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div>
      <div className="text-xs font-semibold "
        style={{ color: 'var(--st-mute)' }}>{title}</div>
      {subtitle && (
        <div className="text-xs mt-0.5" style={{ color: 'var(--st-mute)' }}>{subtitle}</div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Production screen — Cinematic production console + elapsed timer
// ═══════════════════════════════════════════════════════════════════════════
function ProductionScreen({ video, lessonTitle, onViewLibrary }) {
  // Coerce started_at to tz-aware before parsing — older API responses
  // returned naive UTC strings which JS reads as local time (TIMECODE jumped
  // by the user's UTC offset on remount). Defensive belt-and-braces fix.
  const startedAtMs = useMemo(() => {
    const s = video.started_at;
    if (!s) return Date.now();
    if (typeof s !== 'string') {
      const t = new Date(s).getTime();
      return isFinite(t) ? t : Date.now();
    }
    const safe = (s.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(s)) ? s : s + 'Z';
    const t = new Date(safe).getTime();
    return isFinite(t) ? t : Date.now();
  }, [video.started_at]);

  const [elapsed, setElapsed] = useState(() =>
    Math.max(0, (Date.now() - startedAtMs) / 1000)
  );
  useEffect(() => {
    const t = setInterval(() => setElapsed(Math.max(0, (Date.now() - startedAtMs) / 1000)), 1000);
    return () => clearInterval(t);
  }, [startedAtMs]);

  const accent = STUDIO_ACCENT;
  return (
    <div className="space-y-3" data-testid="video-studio-cooking">
      <CinematicProduction
        tint={accent}
        elapsedSeconds={elapsed}
        mode={video.mode}
        lessonTitle={video.lesson_title || lessonTitle}
        genreLabel={video.genre_label || prettifyGenreId(video.genre)}
      />
      <div className="flex items-center justify-between gap-3 px-2">
        <div className="text-xs" style={{ color: 'var(--st-mute)' }}>
          Production is underway. You can switch tabs freely — the film keeps
          producing in the background and the final master will be waiting in
          your Library when delivery is complete.
        </div>
        <button onClick={onViewLibrary}
          data-testid="video-studio-cooking-back"
          className="text-xs font-semibold px-3 py-1.5 rounded-full border whitespace-nowrap inline-flex items-center gap-1.5"
          style={{ borderColor: 'var(--st-border)', color: 'var(--st-ink)' }}>
          <History className="w-3 h-3" /> View Library
        </button>
      </div>
    </div>
  );
}

// Best-effort fallback when the backend hasn't returned the canonical
// `genre_label` yet (e.g. between the optimistic Produce click and the first
// fetchVideo round-trip).
function prettifyGenreId(id) {
  if (!id) return '';
  return id.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
}

function FailedCard({ video, onReset }) {
  return (
    <div data-testid="video-studio-failed"
      className="border p-8 text-center"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
      <div className="text-xs font-semibold mb-3"
        style={{ color: '#DC2626' }}>
        Production halted
      </div>
      <div className="text-[22px] font-semibold leading-tight mb-2 max-w-md mx-auto"
        style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
        The shoot encountered an unexpected issue.
      </div>
      <div className="text-xs mb-6 max-w-md mx-auto leading-relaxed"
        style={{ color: 'var(--st-mute)' }}>
        {video?.failure_reason || 'Some shots could not be captured. You can restart production with the same setup.'}
      </div>
      <button onClick={onReset}
        data-testid="video-studio-failed-reset"
        className="text-xs font-semibold px-5 py-2.5 inline-flex items-center gap-2"
        style={{ background: 'var(--st-ink)', color: 'var(--st-bg)' }}>
        Restart production
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Ready screen — embedded Prime-style video player
// ═══════════════════════════════════════════════════════════════════════════
function ReadyScreen({ video, onClose }) {
  const accent = STUDIO_ACCENT;
  return (
    <div className="space-y-3" data-testid="video-studio-ready">
      {(video.warnings || []).length > 0 && (
        <div
          data-testid="video-studio-warnings"
          className="flex items-start gap-2.5 rounded-xl px-4 py-3 text-xs"
          style={{ background: 'rgba(240,138,36,0.1)', border: '1px solid rgba(240,138,36,0.3)', color: 'var(--st-ink)' }}>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: STUDIO_ACCENT }} />
          <div className="space-y-1">
            {video.warnings.map((w, i) => <p key={i} data-testid={`video-studio-warning-${i}`}>{w}</p>)}
          </div>
        </div>
      )}
      <DeskeyVideoPlayer video={video} accent={accent} embedded
        onClose={onClose} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Library screen — list cooked videos for this lesson
// ═══════════════════════════════════════════════════════════════════════════
function LibraryScreen({ lessonId, onOpen }) {
  const [items, setItems] = useState(null);
  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/student/lesson/${lessonId}/videos`);
      setItems(r.items || []);
    } catch { setItems([]); }
  }, [lessonId]);
  useEffect(() => { reload(); }, [reload]);

  const remove = async (id) => {
    if (!(await confirmDialog({ title: 'Delete video', message: 'Delete this video?', danger: true, confirmLabel: 'Delete' }))) return;
    try {
      await api.delete(`/student/video/${id}`);
      toast.success('Deleted.');
      reload();
    } catch { toast.error('Could not delete.'); }
  };

  if (items === null) {
    return (
      <div className="text-xs font-semibold py-12 text-center"
        style={{ color: 'var(--st-mute)' }}>
        Loading catalog…
      </div>
    );
  }
  if (!items.length) {
    return (
      <div data-testid="video-studio-library-empty"
        className="border p-12 text-center"
        style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
        <div className="text-xs font-semibold mb-3"
          style={{ color: 'var(--st-mute)' }}>The catalog is empty</div>
        <div className="text-[22px] font-semibold leading-tight mb-2 max-w-md mx-auto"
          style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
          Your first commission is one tap away.
        </div>
        <div className="text-xs max-w-sm mx-auto leading-relaxed"
          style={{ color: 'var(--st-mute)' }}>
          Head back to <strong style={{ color: 'var(--st-ink)' }}>Commission</strong> to
          start production on the first film of this lesson.
        </div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5"
      data-testid="video-studio-library">
      {items.map(it => {
        const accent = STUDIO_ACCENT;
        const min = Math.floor((it.duration_seconds || 0) / 60);
        const sec = Math.floor((it.duration_seconds || 0) % 60);
        const statusLabel =
          it.status === 'cooking' ? 'In Production' :
          it.status === 'failed'  ? 'Production Halted' :
          it.status === 'ready'   ? 'Master Delivered' : it.status;
        const canOpen = it.status === 'ready' || it.status === 'cooking';
        const isReady = it.status === 'ready';
        return (
          <div key={it.id}
            data-testid={`video-studio-library-item-${it.id}`}
            className="group relative border overflow-hidden flex flex-col transition-colors"
            style={{
              aspectRatio: '2 / 3',
              borderColor: 'var(--st-border)',
              background:  'var(--st-card-2, var(--st-card))',
            }}>
            {/* Poster art well — gradient placeholder until we have thumbnails */}
            <button onClick={() => canOpen && onOpen(it)} disabled={!canOpen}
              data-testid={`video-studio-library-open-${it.id}`}
              className="absolute inset-0 w-full h-full text-left disabled:cursor-not-allowed"
              style={{
                background: `linear-gradient(160deg, ${accent} 0%, color-mix(in srgb, ${accent} 50%, #1F1F2A) 100%)`,
              }}>
              {/* Subtle film grain over the poster */}
              <span aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-[0.10] mix-blend-overlay"
                style={{
                  backgroundImage: 'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)',
                  backgroundSize:  '3px 3px',
                }} />
            </button>

            {/* Status stamp top-left, like a film festival selection mark */}
            <span data-testid={`video-studio-library-status-${it.id}`}
              className="absolute top-3 left-3 z-10 px-2 py-1 text-xs font-semibold "
              style={{
                background: isReady ? 'var(--st-ink)' : 'var(--st-bg)',
                color:      isReady ? 'var(--st-bg)' : 'var(--st-ink)',
                border:     '1px solid var(--st-border)',
              }}>
              {statusLabel}
            </span>

            {/* Delete corner button */}
            <button onClick={(e) => { e.stopPropagation(); remove(it.id); }}
              data-testid={`video-studio-library-delete-${it.id}`}
              aria-label="Delete from catalog"
              className="absolute top-3 right-3 z-10 w-7 h-7 inline-flex items-center justify-center transition-colors hover:bg-black/30"
              style={{ color: 'rgba(255,255,255,0.85)' }}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>

            {/* Warning stamp — e.g. narration audio missing. Visible before
                opening so the student doesn't have to guess why a video is
                silent. */}
            {isReady && (it.warnings || []).length > 0 && (
              <span
                data-testid={`video-studio-library-warning-${it.id}`}
                title={it.warnings.join(' ')}
                className="absolute bottom-[52px] right-3 z-10 w-6 h-6 inline-flex items-center justify-center rounded-full"
                style={{ background: STUDIO_ACCENT, color: '#1A1300' }}>
                <AlertTriangle className="w-3.5 h-3.5" />
              </span>
            )}

            {/* Title plate — paper strip pinned to the bottom of the poster */}
            <div className="relative z-10 mt-auto px-4 py-3 border-t pointer-events-none"
              style={{
                background:  'var(--st-bg)',
                borderColor: 'var(--st-border)',
              }}>
              <div className="text-xs font-semibold "
                style={{ color: featureInk(accent) }}>
                {it.mode === 'story' ? 'Story · ' : 'Explainer · '}
                {it.genre_label || (min || sec ? `${min}:${sec.toString().padStart(2,'0')}` : '—')}
              </div>
              <div className="text-sm font-semibold leading-tight mt-0.5 line-clamp-2"
                style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
                {it.title || 'Untitled commission'}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Prime-style video player (embedded)
// ═══════════════════════════════════════════════════════════════════════════
function DeskeyVideoPlayer({ video, accent, embedded, onClose }) {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [muted, setMuted] = useState(false);
  const [hideUi, setHideUi] = useState(false);
  const [showSpeed, setShowSpeed] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [hoverPct, setHoverPct] = useState(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const blobUrlRef = useRef(null);

  // Kill any rogue audio/video elements outside this player.
  const killOtherMedia = useCallback(() => {
    const me = videoRef.current;
    document.querySelectorAll('audio, video').forEach((el) => {
      if (el === me) return;
      try {
        el.pause();
        el.muted = true;
        if (el.tagName === 'AUDIO') {
          el.removeAttribute('src');
          try { el.load(); } catch (_) { /* noop */ }
        }
      } catch (_) { /* noop */ }
    });
  }, []);
  useEffect(() => {
    killOtherMedia();
    const t = setInterval(killOtherMedia, 1500);
    return () => clearInterval(t);
  }, [killOtherMedia]);

  const chapters = useMemo(
    () => Array.isArray(video?.video_chapters) ? video.video_chapters : [],
    [video?.video_chapters],
  );
  const panelChapters = useMemo(
    () => chapters.filter(c => (c.duration_seconds || 0) > 0),
    [chapters],
  );
  const currentChapter = useMemo(() => {
    let active = panelChapters[0];
    for (const c of panelChapters) if (current >= (c.start_seconds || 0)) active = c;
    return active;
  }, [current, panelChapters]);

  // Fetch the MP4 via authed blob → object URL
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = TOK();
        const r = await fetch(`${API_ROOT}/student/video/${video.id}/play`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok) throw new Error(`video ${r.status}`);
        const b = await r.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(b);
        blobUrlRef.current = url;
        setVideoUrl(url);
      } catch (_) {
        toast.error('Could not load the video.');
        if (onClose) onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        try { URL.revokeObjectURL(blobUrlRef.current); } catch (_) { /* noop */ }
        blobUrlRef.current = null;
      }
    };
  }, [video.id, onClose]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) { killOtherMedia(); v.play().catch(() => setPlaying(false)); }
    else v.pause();
  }, [playing, videoUrl, killOtherMedia]);
  useEffect(() => { if (videoRef.current) videoRef.current.playbackRate = speed; }, [speed]);
  useEffect(() => { if (videoRef.current) videoRef.current.muted = muted; }, [muted]);

  useEffect(() => {
    const t = setTimeout(() => setShowIntro(false), 3200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    setHideUi(false);
    const t = setTimeout(() => setHideUi(true), 2600);
    const m = () => { setHideUi(false); clearTimeout(t); };
    const el = containerRef.current;
    if (el) { el.addEventListener('mousemove', m); el.addEventListener('touchstart', m); }
    return () => {
      clearTimeout(t);
      if (el) { el.removeEventListener('mousemove', m); el.removeEventListener('touchstart', m); }
    };
  }, [playing, current]);

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el.requestFullscreen?.();
  };

  const onTimeUpdate = () => {
    const v = videoRef.current; if (!v) return;
    setCurrent(v.currentTime || 0);
    try {
      if (v.buffered?.length > 0) setBuffered(v.buffered.end(v.buffered.length - 1));
    } catch (_) { /* noop */ }
  };
  const onLoadedMeta = () => {
    const v = videoRef.current; if (!v) return;
    setDuration(v.duration || 0);
    v.playbackRate = speed;
    killOtherMedia();
    v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };
  const seekTo = (sec) => {
    if (videoRef.current && Number.isFinite(sec)) {
      videoRef.current.currentTime = Math.max(0, Math.min(duration, sec));
    }
  };
  const onScrubberClick = (e) => {
    const bar = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - bar.left) / bar.width));
    seekTo(pct * duration);
  };
  const onScrubberMove = (e) => {
    const bar = e.currentTarget.getBoundingClientRect();
    setHoverPct(Math.max(0, Math.min(1, (e.clientX - bar.left) / bar.width)));
  };
  const fmtTime = (s) => {
    if (!Number.isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const progressPct = duration ? (current / duration) * 100 : 0;
  const bufferedPct = duration ? (buffered / duration) * 100 : 0;
  const hoverChapter = useMemo(() => {
    if (hoverPct === null || !duration || !panelChapters.length) return null;
    const t = hoverPct * duration;
    let ac = panelChapters[0];
    for (const c of panelChapters) if (t >= (c.start_seconds || 0)) ac = c;
    return { t, ch: ac };
  }, [hoverPct, duration, panelChapters]);

  return (
    <div ref={containerRef}
      data-testid="deskey-video-player"
      className="relative bg-black rounded-3xl overflow-hidden select-none isolate"
      style={{ aspectRatio: '16 / 9', boxShadow: '0 30px 80px -30px rgba(0,0,0,0.7)' }}>
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white">
          <Loader />
          <div className="text-xs font-semibold opacity-80">
            Plating your video…
          </div>
        </div>
      )}

      {videoUrl && (
        <video
          ref={videoRef} src={videoUrl} autoPlay playsInline
          onTimeUpdate={onTimeUpdate} onLoadedMetadata={onLoadedMeta}
          onPlay={() => { setPlaying(true); killOtherMedia(); }}
          onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
          onClick={(e) => { e.stopPropagation(); setPlaying(p => !p); }}
          data-testid="deskey-video-player-video"
          className="absolute inset-0 w-full h-full object-cover cursor-pointer"
        />
      )}

      {/* Vignette + grain */}
      <div className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(circle at 50% 55%, transparent 55%, rgba(0,0,0,0.55) 100%)' }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)', backgroundSize: '3px 3px' }} />

      {/* Intro */}
      {showIntro && !loading && (
        <div data-testid="deskey-video-player-intro"
          className="pointer-events-none absolute inset-0 flex items-end pb-20 sm:pb-28 px-6 sm:px-12"
          style={{
            background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.25) 45%, transparent 70%)',
            animation: 'dvIntroFade 0.6s ease-out, dvIntroOut 0.5s ease-in 2.7s forwards',
          }}>
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-white px-2 py-0.5 rounded shadow-lg"
                style={{ background: accent }}>Desky Originals</span>
              <span className="text-white/85 text-xs font-semibold ">
                {video.mode === 'story' ? 'Story Mode' : 'Explain Mode'}
              </span>
            </div>
            <div className="text-white text-3xl sm:text-5xl font-semibold leading-[1.04] drop-shadow-2xl"
              style={{ fontFamily: 'var(--st-font-heading)' }}>{video.title}</div>
            {video.logline && (
              <div className="text-white/85 text-sm sm:text-[15.5px] mt-2.5 max-w-2xl drop-shadow">
                {video.logline}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top bar */}
      <div data-testid="deskey-video-player-topbar"
        className={`absolute top-0 left-0 right-0 px-3 sm:px-5 py-3 sm:py-4 flex items-center gap-2 sm:gap-3 transition-opacity duration-300 ${hideUi && playing ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.85) 0%, transparent 100%)' }}>
        <button onClick={onClose} data-testid="deskey-video-player-close"
          className="w-10 h-10 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center hover:bg-white/25 transition-colors">
          <X className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0 text-white">
          <div className="text-xs sm:text-[10px] font-semibold opacity-80 flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
            {video.mode === 'story' ? 'Story Mode' : 'Explain Mode'} · {video.lesson_title}
          </div>
          <div className="text-sm sm:text-[15.5px] font-semibold truncate"
            style={{ fontFamily: 'var(--st-font-heading)' }}>{video.title}</div>
        </div>
        <button onClick={() => setMuted(m => !m)} data-testid="deskey-video-player-mute"
          className="w-10 h-10 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center hover:bg-white/25 transition-colors">
          {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
        <button onClick={toggleFullscreen} data-testid="deskey-video-player-fullscreen"
          className="w-10 h-10 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center hover:bg-white/25 transition-colors">
          <Film className="w-5 h-5" />
        </button>
      </div>

      {/* Center play */}
      {!playing && !loading && (
        <button onClick={(e) => { e.stopPropagation(); setPlaying(true); }}
          data-testid="deskey-video-player-center-play"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-20 h-20 sm:w-24 sm:h-24 rounded-full text-white flex items-center justify-center transition-transform "
          style={{
            background: accent,
            boxShadow: `0 18px 50px -10px ${accent}, 0 0 0 14px ${accent}1a, 0 0 0 28px ${accent}0d`,
          }}>
          <Play className="w-10 h-10 sm:w-12 sm:h-12 ml-1" />
        </button>
      )}

      {/* Bottom controls */}
      <div data-testid="deskey-video-player-controls"
        className={`absolute bottom-0 left-0 right-0 px-3 sm:px-6 pb-3 sm:pb-5 transition-opacity duration-300 ${hideUi && playing ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.55) 55%, transparent 100%)' }}>
        <div className="max-w-6xl mx-auto pt-10">
          {currentChapter?.caption && (
            <div data-testid="deskey-video-player-caption"
              className="text-white text-sm sm:text-[15.5px] mb-3 max-w-3xl drop-shadow font-semibold leading-snug">
              {currentChapter.caption}
            </div>
          )}

          <div className="relative mb-2.5">
            {hoverChapter && (
              <div className="absolute -top-12 -translate-x-1/2 px-2.5 py-1 rounded-lg bg-black/90 border border-white/15 text-white text-xs font-semibold whitespace-nowrap pointer-events-none"
                style={{ left: `${(hoverPct || 0) * 100}%` }}>
                <span className="text-white/60 mr-1">Ch {hoverChapter.ch.index}</span>
                {fmtTime(hoverChapter.t)}
              </div>
            )}
            <div className="relative h-2.5 hover:h-3 transition bg-white/15 rounded-full cursor-pointer group"
              onClick={onScrubberClick} onMouseMove={onScrubberMove} onMouseLeave={() => setHoverPct(null)}
              data-testid="deskey-video-player-scrubber">
              <div className="absolute inset-y-0 left-0 bg-white/30 rounded-full" style={{ width: `${bufferedPct}%` }} />
              <div className="absolute inset-y-0 left-0 rounded-full"
                style={{ width: `${progressPct}%`, background: `linear-gradient(90deg, ${accent}, color-mix(in srgb, ${accent} 70%, #fff))` }} />
              {panelChapters.map((c, i) => {
                const left = duration ? (c.start_seconds / duration) * 100 : 0;
                if (left <= 0.2) return null;
                return <div key={i} className="absolute top-1/2 -translate-y-1/2 w-0.5 h-2.5 bg-black/45" style={{ left: `${left}%` }} />;
              })}
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `${progressPct}%`, background: '#fff', boxShadow: `0 0 0 6px ${accent}40` }} />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 text-white">
            <div className="text-xs sm:text-[12px] font-semibold tabular-nums opacity-90 shrink-0">
              <span data-testid="deskey-video-player-time-current">{fmtTime(current)}</span>
              <span className="opacity-50 mx-1">/</span>
              <span data-testid="deskey-video-player-time-total">{fmtTime(duration)}</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2.5">
              <button onClick={() => seekTo(current - SKIP_SECONDS)}
                data-testid="deskey-video-player-skip-back"
                className="relative w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors">
                <ChevronLeft className="w-5 h-5" />
                <span className="absolute -bottom-0.5 text-xs font-semibold opacity-90">10</span>
              </button>
              <button onClick={() => setPlaying(p => !p)}
                data-testid="deskey-video-player-play"
                className="w-14 h-14 rounded-full text-white flex items-center justify-center shadow-lg transition-transform "
                style={{ background: accent, boxShadow: `0 12px 30px -10px ${accent}` }}>
                {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
              </button>
              <button onClick={() => seekTo(current + SKIP_SECONDS)}
                data-testid="deskey-video-player-skip-fwd"
                className="relative w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors">
                <ChevronRight className="w-5 h-5" />
                <span className="absolute -bottom-0.5 text-xs font-semibold opacity-90">10</span>
              </button>
            </div>
            <div className="relative shrink-0">
              <button onClick={() => setShowSpeed(s => !s)}
                data-testid="deskey-video-player-speed"
                className="px-3 h-10 rounded-full bg-white/15 hover:bg-white/25 text-xs font-semibold tabular-nums transition-colors">
                {speed}×
              </button>
              {showSpeed && (
                <div data-testid="deskey-video-player-speed-menu"
                  className="absolute right-0 bottom-12 bg-black/95 backdrop-blur rounded-xl border border-white/15 py-1 min-w-[110px] shadow-lg">
                  <div className="text-xs font-semibold text-white/50 px-3.5 py-1.5">Speed</div>
                  {PRIME_SPEEDS.map(s => (
                    <button key={s}
                      data-testid={`deskey-video-player-speed-${s}`}
                      onClick={() => { setSpeed(s); setShowSpeed(false); }}
                      className={`w-full text-left px-3.5 py-1.5 text-xs font-semibold transition-colors ${s === speed ? 'text-white bg-white/15' : 'text-white/70 hover:text-white hover:bg-white/10'}`}>
                      {s === 1 ? '1× · Normal' : `${s}×`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes dvIntroFade { from { opacity: 0; transform: translateY(12px);} to { opacity: 1; transform: translateY(0);} }
        @keyframes dvIntroOut  { from { opacity: 1;} to { opacity: 0;} }
      `}</style>
    </div>
  );
}
